import React, { useEffect, useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getRoomConfigs } from "../../../queries";
import { useFormContext } from "react-hook-form";
import { OrderContext } from "../../../contexts/order_context";
import RoomConfig from "./room_config";
import SpinLoading from "../../../../shared/spin_loading";
import { isPresent } from "../../../../../helpers/application_helper";

const Rooms = ({ selectedTermId, isExtra }) => {
  const { termId } = useContext(OrderContext);
  const [roomsData, setRoomsData] = useState([]);

  const refName = isExtra ? `extra_room_configs` : `room_configs`;

  const {
    watch,
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();

  const adults = watch("adults");
  const children = watch("children");
  const roomCount = watch("rooms");
  const selected = watch(refName);
  const selectedCombination = watch(`${refName}_selector`);

  const roomConfigsQuery = useQuery(
    ["roomConfigs", { termId: selectedTermId || termId }],
    () =>
      getRoomConfigs({
        termId: selectedTermId || termId,
        adults: adults.value,
        children: children.value,
        roomCount: roomCount.value,
        selected: selected,
      }),
    {
      onSuccess: (data) => {
        setRoomsData(data);
        setValue(`${refName}_people`, undefined);
        if (data.length === 0) clearSelectedRoomConfigs();
        data.forEach((room) => {
          if (roomCount.value === 2) {
            handleTwoRooms(room);
          } else {
            handleOneRoom(room);
          }
        });
      },
      enabled:
        isPresent(selectedTermId || termId) &&
        isPresent(adults?.value) &&
        isPresent(children?.value) &&
        isPresent(roomCount?.value) &&
        isPresent(selected),
    }
  );

  // People count of the selected room configs, price calculation waits until it matches the order
  const roomConfigPeople = (roomConfig) =>
    roomConfig.adults + roomConfig.children;

  const clearSelectedRoomConfigs = () => {
    if (getValues(`${refName}.one.id`) != undefined)
      setValue(`${refName}.one.id`, undefined);
    if (getValues(`${refName}.two.id`) != undefined)
      setValue(`${refName}.two.id`, undefined);
  };

  const handleOneRoom = (room) => {
    if (!room.selected) return;

    if (getValues(`${refName}.one.id`) != room.id)
      setValue(`${refName}.one.id`, room.id);
    if (getValues(`${refName}.two.id`) != undefined)
      setValue(`${refName}.two.id`, undefined);
    setValue(`${refName}_people`, roomConfigPeople(room));
  };

  const handleTwoRooms = (rooms) => {
    if (!rooms[0].selected) return;

    if (getValues(`${refName}.one.id`) != rooms[0].id)
      setValue(`${refName}.one.id`, rooms[0].id);
    if (getValues(`${refName}.two.id`) != rooms[1].id)
      setValue(`${refName}.two.id`, rooms[1].id);
    setValue(
      `${refName}_people`,
      roomConfigPeople(rooms[0]) + roomConfigPeople(rooms[1])
    );
  };

  const handleSetRoomsValue = (value) => {
    if (getValues("rooms") != { label: value, value: value })
      setValue("rooms", { label: value, value: value });
  };

  useEffect(() => {
    if (typeof selectedCombination === "undefined") return;

    if (getValues(`${refName}.one.id`) != selectedCombination.split(" ")[0])
      setValue(`${refName}.one.id`, selectedCombination.split(" ")[0]);
    if (getValues(`${refName}.two.id`) != selectedCombination.split(" ")[1])
      setValue(`${refName}.two.id`, selectedCombination.split(" ")[1]);
  }, [selectedCombination]);

  useEffect(() => {
    if (
      !isPresent(adults?.value) ||
      !isPresent(children?.value) ||
      !isPresent(roomCount?.value)
    )
      return;

    roomConfigsQuery.refetch();
  }, [roomCount, adults, children, selected?.one?.id, selected?.two?.id]);

  if (!roomConfigsQuery.isFetched || !roomCount) {
    return (
      <div className="form-check">
        <SpinLoading show={true} />
      </div>
    );
  }

  if (roomsData.length === 0) {
    return (
      <>
        <div className="alert alert-danger">
          {I18n.t("js.order.accomodation.no_results")}
        </div>
        {roomCount.value == 2 ? (
          <a
            className="btn btn-primary btn-md show_one_room_config"
            onClick={() => handleSetRoomsValue(1)}
          >
            {I18n.t("js.order.accomodation.show_one_room")}
          </a>
        ) : (
          <a
            className="btn btn-primary btn-md show_two_rooms_config"
            onClick={() => handleSetRoomsValue(2)}
          >
            {I18n.t("js.order.accomodation.show_two_rooms")}
          </a>
        )}
      </>
    );
  }

  return (
    <div className="form-check">
      {roomCount.value == 1
        ? roomsData.map((roomConfig, index) => {
            if (typeof roomConfig.id === "undefined") return null;
            return (
              <React.Fragment key={`room_config-${roomConfig.id}-${index}`}>
                <RoomConfig
                  key={`room_config1-${roomConfig.id}`}
                  roomConfig={roomConfig}
                  register={register}
                  visibleRadio={true}
                  isExtra={isExtra}
                  number={"one"}
                />
                {roomsData.length == index + 1 ? (
                  <></>
                ) : (
                  <hr className="mr-3" />
                )}
              </React.Fragment>
            );
          })
        : roomsData.map((roomConfigCombination, index) => {
            if (!roomConfigCombination[0] || !roomConfigCombination[1])
              return null;
            return (
              <React.Fragment
                key={`room_config_combination-${roomConfigCombination[0].id}-${roomConfigCombination[1].id}`}
              >
                <div className="hidden">
                  {roomConfigCombination[0] && roomConfigCombination[1] && (
                    <input
                      className="accomodation_checkbox"
                      id={`room_config_combination-${roomConfigCombination[0].id}-${roomConfigCombination[1].id}`}
                      type="radio"
                      value={`${roomConfigCombination[0].id} ${roomConfigCombination[1].id}`}
                      defaultChecked={
                        (selectedCombination &&
                          selectedCombination[0] ==
                            roomConfigCombination[0].id &&
                          selectedCombination[1] ==
                            roomConfigCombination[1].id) ||
                        index === 0
                      }
                      {...register(`${refName}_selector`)}
                    />
                  )}
                </div>
                <label
                  className="w-100"
                  htmlFor={`room_config_combination-${roomConfigCombination[0].id}-${roomConfigCombination[1].id}`}
                >
                  {roomConfigCombination[0] && (
                    <RoomConfig
                      key={`double-first-${roomConfigCombination[0].id}-${index}`}
                      roomConfig={roomConfigCombination[0]}
                      isExtra={isExtra}
                      register={register}
                      visibleRadio={false}
                      number={"one"}
                      htmlFor={`room_config_combination-${roomConfigCombination[0].id}-${roomConfigCombination[1].id}`}
                    />
                  )}
                  {roomConfigCombination[1] && (
                    <RoomConfig
                      key={`double-second-${roomConfigCombination[1].id}-${index}`}
                      roomConfig={roomConfigCombination[1]}
                      isExtra={isExtra}
                      register={register}
                      visibleRadio={false}
                      number={"two"}
                      htmlFor={`room_config_combination-${roomConfigCombination[0].id}-${roomConfigCombination[1].id}`}
                    />
                  )}
                </label>
                {roomsData.length == index + 1 ? (
                  <></>
                ) : (
                  <hr className="room-divider" />
                )}
              </React.Fragment>
            );
          })}
    </div>
  );
};

export default Rooms;
